import { useMemo, useState } from "react";
import { CATEGORY_LABELS, MARKET_LABELS, type Category, type Signal } from "@shared/schema/signal";
import { directionGlyph, formatChange } from "../lib";
import { Sparkline } from "../components/Sparkline";

const categories = Object.keys(CATEGORY_LABELS) as Category[];

function MatrixCell({ signal }: { signal: Signal }) {
  return (
    <article className="matrix-cell">
      <div className="matrix-cell-heading"><strong>{signal.metricId}</strong><span>{MARKET_LABELS[signal.market]}</span></div>
      <h3>{signal.title}</h3>
      <div className="matrix-value"><strong>{signal.displayValue}</strong><span className={`direction-${signal.direction}`}>{directionGlyph(signal.direction)} {formatChange(signal.change1d)}</span></div>
      <Sparkline values={signal.history.map((point) => point.value)} direction={signal.direction} width={150} height={36} />
      <div className="matrix-meta"><span>{signal.percentile1y === undefined ? "—" : `${signal.percentile1y.toFixed(0)}%`} <small>1Y percentile</small></span><span>{signal.source.name}</span></div>
    </article>
  );
}

export function SignalMatrix({ signals }: { signals: Signal[] }) {
  const initial = useMemo(() => new Set<Category>(), []);
  const [collapsed, setCollapsed] = useState(initial);
  const toggle = (category: Category) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(category)) next.delete(category); else next.add(category);
    return next;
  });
  return (
    <section className="matrix-view view-enter" aria-labelledby="matrix-title">
      <div className="view-heading compact-heading">
        <div><span className="eyebrow">SIGNAL MATRIX</span><h1 id="matrix-title">所有指标现在是什么状态？</h1></div>
        <p>{signals.length} 个指标 · 按分类查看当前值、方向、历史分位和数据状态。</p>
      </div>
      <div className="matrix-groups">
        {categories.map((category) => {
          const items = signals.filter((signal) => signal.category === category).sort((a, b) => b.anomalyScore - a.anomalyScore);
          if (items.length === 0) return null;
          const isCollapsed = collapsed.has(category);
          return (
            <section className="matrix-group" key={category}>
              <button type="button" className="matrix-group-title" onClick={() => toggle(category)} aria-expanded={!isCollapsed}>
                <span><strong>{CATEGORY_LABELS[category]}</strong><small>{items.length} 个指标</small></span><i>{isCollapsed ? "+" : "−"}</i>
              </button>
              {!isCollapsed ? <div className="matrix-grid">{items.map((signal) => <MatrixCell signal={signal} key={signal.id} />)}</div> : null}
            </section>
          );
        })}
        {signals.length === 0 ? <p className="empty-state">当前筛选下没有指标。</p> : null}
      </div>
    </section>
  );
}
